import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { useHistory, useLocation } from 'react-router-dom'
import Button from '@material-ui/core/Button'
import CoachAddStudentModal from 'components/add-student-modal/coach-add-student-modal'
import { gtmEvent } from 'lib/gtm'
import { FormShell } from './form-parts'
import StudentPicker from './student-picker'
import useStyles from './styles'

// Signed-in coach booking one of their students. Same data flow as the legacy
// `CoachView`: search the roster, pick a student (or add one), and push
// `&studentId=...` onto the current URL. A `?student=<id>` query param
// pre-selects that student and skips ahead, as before.
const CoachStudentForm = ({
  students,
  currentUrl,
  eventObject,
  submitLabel,
  priceLabel,
  belowCard,
}) => {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const [selectedStudentId, setSelectedStudentId] = useState(null)
  const [openAddStudent, setOpenAddStudent] = useState(false)
  const [error, setError] = useState(null)
  const roster = students || []
  const studentPreSelect = new URLSearchParams(location.search).get('student')

  useEffect(() => {
    if (studentPreSelect && roster.length > 0) {
      history.push(`${currentUrl}&studentId=${studentPreSelect}`)
      gtmEvent({
        event: 'formSubmit',
        formCategory: 'book-session',
        formAction: 'pre-selected-student',
      })
    }
    // Only the first render decides whether to skip ahead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSubmit = event => {
    event.preventDefault()
    if (!selectedStudentId) {
      setError('Select a student to continue.')
      return
    }
    history.push(`${currentUrl}&studentId=${selectedStudentId}`)
    gtmEvent({ ...eventObject, event: 'select-student-to-schedule' })
    // Remove the below event in the future (legacy name, kept for parity).
    gtmEvent({
      event: 'formSubmit',
      formCategory: 'book-session',
      formAction: 'clicked-add-student-button',
    })
  }

  const handleAddClick = () => {
    setOpenAddStudent(open => !open)
    // Remove the below event in the future.
    gtmEvent({
      event: 'formSubmit',
      formCategory: 'book-session',
      formAction: 'clicked-add-student-button',
    })
  }

  return (
    <>
      <FormShell
        onSubmit={handleSubmit}
        subtitle="Select a student to attend this session."
        submitLabel={submitLabel}
        priceLabel={priceLabel}
        error={error}
        belowCard={belowCard}
      >
        <StudentPicker
          name="coachStudent"
          label="Students"
          students={roster}
          selectedId={selectedStudentId}
          onSelect={id => {
            setSelectedStudentId(id)
            setError(null)
          }}
          searchable
          emptyMessage="You haven't added any students yet."
        />
        <Button
          type="button"
          variant="outlined"
          color="primary"
          className={clsx(classes.outlinedButton, classes.addStudentButton)}
          onClick={handleAddClick}
        >
          Add a new student
        </Button>
      </FormShell>
      <CoachAddStudentModal
        open={openAddStudent}
        setOpen={setOpenAddStudent}
        onAdd={data => {
          const newStudentId = data?.createStudent?.student?.id
          history.push(`${currentUrl}&studentId=${newStudentId}`)
          gtmEvent({
            ...eventObject,
            event: 'add-add-student-to-schedule',
          })
        }}
        dialogTitle="Add a New Student"
        btnTitle="Add Student"
      />
    </>
  )
}

CoachStudentForm.propTypes = {
  students: PropTypes.array,
  currentUrl: PropTypes.string.isRequired,
  eventObject: PropTypes.object,
  submitLabel: PropTypes.string.isRequired,
  priceLabel: PropTypes.string,
  belowCard: PropTypes.node,
}

CoachStudentForm.defaultProps = {
  eventObject: {},
}

export default CoachStudentForm
